import {
    useCallback,
    useMemo,
} from 'react'
import Grid from '@mui/material/Grid'
import isEmpty from 'lodash/isEmpty'

import AdvancedList, {
    ListColumn,
} from 'app/shared-components/AdvancedList/AdvancedList'
import useLabel from 'app/hooks/useLabel'
import {
    PreconditioningTechnology,
    StorageType,
} from 'app/enums/label.enums'
import useEnumValues from 'app/hooks/useEnumValues'
import Select from 'app/shared-components/Select/SelectContainer'
import InputSingleline from 'app/shared-components/InputSingleline'

import {
    StorageTableType,
} from '../../serviceCenters.types'
import useStyles from './Storages.styles'

const getColumns = (l: (l: string) => string): ListColumn[] => {
    return [
        {
            colSpan: 4,
            label: l('STORAGE_TYPE'),
        },
        {
            colSpan: 4,
            label: l('PRECONDITIONING_TECHNOLOGY'),
        },
        {
            colSpan: 'grow',
            label: l('STORAGE_CAPACITY'),
            align: 'right',
        },
        {
            colSpan: 'grow',
            label: l('DAILY_PRECONDITIONING_CAPACITY'),
            align: 'right',
        },
    ]
}

const defaultValue = {
    storageType: undefined,
    preconditioningTechnology: undefined,
    storageCapacity: undefined,
    dailyPreconditioningCapacity: 0,
}

const Storages = ({
    value = undefined,
    onChange,
    disabled = false,
    required = false,
    exists = false,
    showFields = {},
    disabledFields = {},
}: Readonly<{
    value?: StorageTableType[],
    onChange: (val: StorageTableType[]) => void,
    disabled?: boolean,
    showFields?: Record<string, boolean>
    disabledFields?: Record<string, boolean>
    required?: boolean,
    exists?: boolean,
}>) => {
    const {
        classes,
    } = useStyles()
    const l = useLabel()
    const getAllowedValues = useEnumValues()

    const columns = useMemo(() => {
        return getColumns(l)
    }, [l])

    const onItemCellChange = useCallback(({
        item, newValue, index, handleChange,
    }) => {
        if (newValue?.storageType === StorageType.DRY) {
            handleChange({
                ...item,
                ...newValue,
                preconditioningTechnology: undefined,
                dailyPreconditioningCapacity: undefined,
            }, index)

            return
        }

        handleChange({
            ...item,
            ...newValue,
        }, index)
    }, [])

    const validateItemForAdd = useCallback((item: StorageTableType): boolean => {
        return !isEmpty(item)
            && (
                (item.storageType === StorageType.DRY && Boolean(item.storageCapacity))
            || Object.values(item).every((el) => {
                return Boolean(el) || el === 0
            }))
    }, [])

    const renderItem = useCallback(({
        index,
        item,
        handleChange,
    }) => {
        return (
            <>
                <Grid
                    key={`row-storageType-${index}`}
                    size={columns[0].colSpan}
                    p={1}
                >
                    <Select
                        name="storageType"
                        allowedValues={getAllowedValues(StorageType)}
                        value={item?.storageType}
                        onChange={(newValue) => {
                            return onItemCellChange({
                                newValue: {
                                    storageType: newValue,
                                },
                                index,
                                item,
                                handleChange,
                            })
                        }}
                        required={required}
                        disabled={
                            !showFields.storageType && (disabledFields.storageType || disabled)
                        }
                    />
                </Grid>
                <Grid
                    key={`row-preconditioningTechnology-${index}`}
                    size={columns[1].colSpan}
                    p={1}
                >
                    <Select
                        name="preconditioningTechnology"
                        allowedValues={getAllowedValues(PreconditioningTechnology)}
                        value={item?.preconditioningTechnology}
                        onChange={(newValue) => {
                            return onItemCellChange({
                                newValue: {
                                    preconditioningTechnology: newValue,
                                },
                                index,
                                item,
                                handleChange,
                            })
                        }}
                        required={required}
                        disabled={item.storageType === StorageType.DRY
                            || (!showFields.preconditioningTechnology
                            && (disabledFields.preconditioningTechnology || disabled))}
                    />
                </Grid>
                <Grid
                    key={`row-storageCapacity-${index}`}
                    size={columns[2].colSpan}
                    p={1}
                >
                    <InputSingleline
                        name="storageCapacity"
                        type="number"
                        min={1}
                        centerAligned
                        onChange={(newValue) => {
                            return onItemCellChange({
                                index,
                                item,
                                handleChange,
                                newValue: {
                                    storageCapacity: Number(newValue),
                                },
                            })
                        }}
                        className={classes.input}
                        value={item?.storageCapacity}
                        required={required}
                        disabled={!showFields.storageCapacity
                            && (disabledFields.storageCapacity || disabled)}
                    />
                </Grid>
                <Grid
                    key={`row-dailyPreconditioningCapacity-${index}`}
                    size={columns[3].colSpan}
                    p={1}
                >
                    <InputSingleline
                        name="dailyPreconditioningCapacity"
                        type="number"
                        min={1}
                        centerAligned
                        onChange={(newValue) => {
                            return onItemCellChange({
                                index,
                                item,
                                handleChange,
                                newValue: {
                                    dailyPreconditioningCapacity: Number(newValue),
                                },
                            })
                        }}
                        className={classes.input}
                        value={item?.dailyPreconditioningCapacity}
                        required={required}
                        disabled={item.storageType === StorageType.DRY
                            || (!showFields.dailyPreconditioningCapacity
                            && (disabledFields.dailyPreconditioningCapacity || disabled))}
                    />
                </Grid>
            </>
        )
    }, [
        columns,
        getAllowedValues,
        required,
        showFields,
        disabledFields,
        disabled,
        classes.input,
        onItemCellChange,
    ])

    if (exists) {
        return (
            <>
                {value && (
                    <AdvancedList<StorageTableType>
                        onChange={onChange}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        value={value}
                        renderItem={renderItem}
                        addButtonLabel="new storage"
                        columns={columns}
                        validateForAdd={validateItemForAdd}
                    />
                )}
            </>
        )
    }

    return (
        <AdvancedList<StorageTableType>
            onChange={onChange}
            defaultValue={defaultValue}
            disabled={disabled}
            value={value}
            renderItem={renderItem}
            addButtonLabel="new storage"
            columns={columns}
            validateForAdd={validateItemForAdd}
        />
    )
}

export default Storages
